import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";

import { ActivityDiscussionContext } from "../../../../../route/shared/activities-dashboard";
import { updateDiscussionMessage } from "../../../../../service/interaction";
import { ServiceError, Status } from "../../../../../service/Shared";
import { useIsLoadingWrapper } from "../../../../../utils";
import { Toast } from "../../../Toast";
import { Message, MessageThread } from "../../models/Message";

interface useDiscussionMessageProps {
  message?: Message;
  setThreadData: (data: MessageThread) => void;
  threadData?: MessageThread;
  userUuid?: string;
  isThreadResolved?: boolean;
}

type useDiscussionMessageReturnData = {
  editable: boolean;
  editedMessage: string;
  editableErrors: ServiceError[] | undefined;
  showEditMessage: boolean;
  showDeleteMessage: boolean;
  showEditThread: boolean;
  showDeleteThread: boolean;
  showResolveThread: boolean;
  showIsUnread: boolean;

  editMessage: () => void;
  setEditable: Dispatch<SetStateAction<boolean>>;
  setEditedMessage: Dispatch<SetStateAction<string>>;
  setEditableErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;
  handleEditMessage: (messageUuid: string) => Promise<void>;
  isHandleEditMessageLoading: boolean;
};

export const useDiscussionMessage = ({
  message,
  setThreadData,
  threadData,
  userUuid,
  isThreadResolved,
}: useDiscussionMessageProps): useDiscussionMessageReturnData => {
  const { selectedThreadDetails, refreshDiscussionTables } = useContext(ActivityDiscussionContext);
  const [editable, setEditable] = useState<boolean>(false);
  const [editedMessage, setEditedMessage] = useState<string>(message?.content || "");
  const [editableErrors, setEditableErrors] = useState<ServiceError[] | undefined>();
  const [isHandleEditMessageLoading, setIsHandleEditMessageLoading] = useState(false);

  const createdByCurrentUser = message?.createdByUserUuid === userUuid;

  const messageIndex = threadData?.messages.findIndex((m) => m.uuid === message?.uuid) ?? -1;
  const noOtherUserMessagesAfter =
    messageIndex === -1 ||
    (threadData?.messages.slice(messageIndex + 1).every((m) => m.createdByUserUuid === userUuid) ?? true);

  const showEditMessage = createdByCurrentUser && noOtherUserMessagesAfter;
  const showDeleteMessage = createdByCurrentUser && noOtherUserMessagesAfter;
  const showEditThread = createdByCurrentUser && noOtherUserMessagesAfter;
  const showDeleteThread = createdByCurrentUser && noOtherUserMessagesAfter;

  const lastMessageInThread = threadData?.messages.at(-1);
  const showIsUnread =
    !!lastMessageInThread &&
    lastMessageInThread.createdByUserUuid !== userUuid &&
    message?.uuid === lastMessageInThread.uuid;
  const showResolveThread = createdByCurrentUser && !isThreadResolved && !showIsUnread && !!threadData?.messages;

  useEffect(() => {
    if (selectedThreadDetails === undefined) {
      setEditable(false);
    }
  }, [selectedThreadDetails]);

  const handleEditMessage = useIsLoadingWrapper(async (messageUuid: string): Promise<void> => {
    const res = await updateDiscussionMessage({
      acceptOrReject: null,
      rejectedReason: null,
      content: editedMessage,
      messageUuid,
      rowVersion: message?.rowVersion ?? 0,
    });

    if (res.status === Status.Success) {
      const messagesCopy = [...(threadData?.messages ?? [])];

      const newMessages = messagesCopy.map((m) => {
        if (m.uuid === messageUuid) {
          return {
            ...m,
            content: editedMessage,
          };
        }

        return m;
      });

      setThreadData({
        resolved: threadData?.resolved ?? false,
        messages: newMessages,
      } as MessageThread);
      refreshDiscussionTables();
      setEditable(false);
      setEditableErrors(undefined);
      Toast.success({ message: "Message updated" });
    } else if (res.status === Status.Error && res.errors) {
      setEditableErrors(res.errors);
    }
  }, setIsHandleEditMessageLoading);

  const editMessage = (): void => {
    setEditable(true);
    setEditedMessage(message?.content || "");
  };

  return {
    editable,
    editedMessage,
    editableErrors,
    showEditMessage,
    showDeleteMessage,
    showEditThread,
    showDeleteThread,
    showResolveThread,
    showIsUnread,
    isHandleEditMessageLoading,
    editMessage,
    setEditable,
    setEditedMessage,
    setEditableErrors,
    handleEditMessage,
  };
};
